import React, { useCallback, useMemo, useState } from "react";
import {
  AppstoreOutlined,
  ClearOutlined,
  FilterOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { FaPlane } from "react-icons/fa";
import { MdHotel } from "react-icons/md";
import FlightResultsScreen from "../flightEngineV2/results/FlightResultsScreen";
import {
  MOCK_AGENT_QUICK_FILTERS,
  MOCK_BAGGAGE_FILTERS,
  MOCK_BOARD_OPTIONS,
  MOCK_DEPARTURE_WINDOWS,
  MOCK_NIGHTS_OPTIONS,
  MOCK_SEATS_FILTERS,
  MOCK_STAR_OPTIONS,
} from "../packagesResultsMockup/mockPackagesData";
import DemoPackageCard from "./DemoPackageCard";
import FlightHotelPassengersPage from "./FlightHotelPassengersPage";
import FlightHotelPaymentPage from "./FlightHotelPaymentPage";
import FlightHotelRoomSelectionPage from "./FlightHotelRoomSelectionPage";
import UnifiedOccupancyField from "./UnifiedOccupancyField";
import {
  UNIFIED_DESTINATIONS,
  cloneSearch,
  createInitialDraftSearch,
  matchFlightFixtures,
  matchPackageFixtures,
  summarizeOccupancy,
  toFe2SearchVm,
} from "./demoFixtures";
import { filterUnifiedFlightHotelCards } from "./filterUnifiedFlightHotelCards";
import { runUnifiedLiveSearch } from "./runUnifiedLiveSearch";
import "../packagesResultsMockup/PackagesResultsMockupPage.css";
import "../flightEngineV2/design-system/tokens.css";
import "../flightEngineV2/results/FlightResultsScreen.css";
import "./UnifiedSearchMockupPage.css";

const TABS = [
  { id: "flightHotel", label: "טיסה + מלון", icon: MdHotel },
  { id: "flights", label: "טיסות בלבד", icon: FaPlane },
];

function DemoEmptyState({ title, body, hint }) {
  return (
    <div className="usm-empty" role="status">
      <p className="usm-empty__title">{title}</p>
      <p className="usm-empty__body">{body}</p>
      {hint ? <p className="usm-empty__hint">{hint}</p> : null}
    </div>
  );
}

export default function UnifiedSearchMockupPage() {
  const [draftSearch, setDraftSearch] = useState(() => createInitialDraftSearch());
  const [appliedSearch, setAppliedSearch] = useState(() =>
    cloneSearch(createInitialDraftSearch())
  );
  const [activeTab, setActiveTab] = useState("flightHotel");
  const [viewMode, setViewMode] = useState("list");
  const [sortBy, setSortBy] = useState("price-asc");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hotelQuery, setHotelQuery] = useState("");
  const [selectedBoards, setSelectedBoards] = useState(() => new Set());
  const [selectedStars, setSelectedStars] = useState(() => new Set());
  const [selectedNights, setSelectedNights] = useState(() => new Set());
  const [agentQuick, setAgentQuick] = useState(() => new Set());
  const [baggageFilter, setBaggageFilter] = useState(() => new Set());
  const [departWindow, setDepartWindow] = useState("all");
  const [seatsFilter, setSeatsFilter] = useState("all");
  const [compareIds, setCompareIds] = useState(() => new Set());
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [selectedFlightId, setSelectedFlightId] = useState(null);
  const [detailPackageId, setDetailPackageId] = useState(null);
  const [bookingDraft, setBookingDraft] = useState(null);
  const [flowStep, setFlowStep] = useState("results"); // results | detail | passengers | payment
  const [toast, setToast] = useState("");
  const [liveSearching, setLiveSearching] = useState(false);
  const [liveFlightHotelCards, setLiveFlightHotelCards] = useState(null);
  const [liveFlightOffers, setLiveFlightOffers] = useState(null);
  const [liveHubwayzData, setLiveHubwayzData] = useState(null);
  const [liveSearchRequest, setLiveSearchRequest] = useState(null);
  const [liveErrors, setLiveErrors] = useState({ hotels: null, flights: null });
  const [useLive, setUseLive] = useState(true);

  const packageMatch = useMemo(
    () => matchPackageFixtures(appliedSearch),
    [appliedSearch]
  );
  const flightMatch = useMemo(
    () => matchFlightFixtures(appliedSearch),
    [appliedSearch]
  );
  const fe2Search = useMemo(() => toFe2SearchVm(appliedSearch), [appliedSearch]);
  const occSummary = useMemo(
    () => summarizeOccupancy(appliedSearch.rooms),
    [appliedSearch]
  );

  const flightHotelCardsRaw =
    useLive && liveFlightHotelCards != null
      ? liveFlightHotelCards
      : packageMatch.items;
  const flightOffers = useLive && liveFlightOffers != null
    ? liveFlightOffers
    : flightMatch.items;
  const flightHotelMatched =
    useLive && liveFlightHotelCards != null
      ? liveFlightHotelCards.length > 0
      : packageMatch.matched;
  const flightsMatched =
    useLive && liveFlightOffers != null
      ? liveFlightOffers.length > 0
      : flightMatch.matched;

  const clearFilters = useCallback(() => {
    setSelectedBoards(new Set());
    setSelectedStars(new Set());
    setSelectedNights(new Set());
    setAgentQuick(new Set());
    setBaggageFilter(new Set());
    setDepartWindow("all");
    setSeatsFilter("all");
    setHotelQuery("");
  }, []);

  const toggleInSet = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const flightHotelCards = useMemo(
    () =>
      filterUnifiedFlightHotelCards(flightHotelCardsRaw, {
        hotelQuery,
        selectedBoards,
        selectedStars,
        selectedNights,
        agentQuick,
        baggageFilter,
        departWindow,
        seatsFilter,
        sortBy,
      }),
    [
      flightHotelCardsRaw,
      hotelQuery,
      selectedBoards,
      selectedStars,
      selectedNights,
      agentQuick,
      baggageFilter,
      departWindow,
      seatsFilter,
      sortBy,
    ]
  );

  const activeChips = useMemo(() => {
    const chips = [];
    if (hotelQuery.trim()) {
      chips.push({
        id: "hotel",
        label: `מלון: ${hotelQuery.trim()}`,
        onRemove: () => setHotelQuery(""),
      });
    }
    MOCK_BOARD_OPTIONS.forEach((o) => {
      if (selectedBoards.has(o.id)) {
        chips.push({
          id: `board-${o.id}`,
          label: o.shortLabel || o.label,
          onRemove: () => toggleInSet(setSelectedBoards, o.id),
        });
      }
    });
    MOCK_AGENT_QUICK_FILTERS.forEach((f) => {
      if (agentQuick.has(f.id)) {
        chips.push({
          id: `agent-${f.id}`,
          label: f.label,
          onRemove: () => toggleInSet(setAgentQuick, f.id),
        });
      }
    });
    MOCK_BAGGAGE_FILTERS.forEach((o) => {
      if (baggageFilter.has(o.id)) {
        chips.push({
          id: `bag-${o.id}`,
          label: o.label,
          onRemove: () => toggleInSet(setBaggageFilter, o.id),
        });
      }
    });
    selectedStars.forEach((s) => {
      chips.push({
        id: `star-${s}`,
        label: `${s}★`,
        onRemove: () => toggleInSet(setSelectedStars, s),
      });
    });
    selectedNights.forEach((n) => {
      chips.push({
        id: `nights-${n}`,
        label: `${n} לילות`,
        onRemove: () => toggleInSet(setSelectedNights, n),
      });
    });
    if (departWindow !== "all") {
      const w = MOCK_DEPARTURE_WINDOWS.find((x) => x.id === departWindow);
      chips.push({
        id: "depart",
        label: w?.label || departWindow,
        onRemove: () => setDepartWindow("all"),
      });
    }
    if (seatsFilter !== "all") {
      const s = MOCK_SEATS_FILTERS.find((x) => x.id === seatsFilter);
      chips.push({
        id: "seats",
        label: s?.label || seatsFilter,
        onRemove: () => setSeatsFilter("all"),
      });
    }
    return chips;
  }, [
    hotelQuery,
    selectedBoards,
    agentQuick,
    baggageFilter,
    selectedStars,
    selectedNights,
    departWindow,
    seatsFilter,
  ]);

  const activeFilterCount = activeChips.length;

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2800);
  }, []);

  const patchDraft = (patch) => {
    setDraftSearch((prev) => {
      const next = { ...prev, ...patch };
      if (patch.destinationId) {
        const dest = UNIFIED_DESTINATIONS.find((d) => d.id === patch.destinationId);
        if (dest) {
          next.destinationLabel = dest.label;
          next.packageDestinationId = dest.packageDestinationId;
          next.flightIata = dest.flightIata;
          next.origin = dest.originDefault || prev.origin;
        }
      }
      return next;
    });
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!draftSearch.destinationId) {
      showToast("נא לבחור יעד");
      return;
    }
    const next = cloneSearch(draftSearch);
    if (next.checkIn && next.checkOut) {
      next.dateRangeLabel = `${next.checkIn} → ${next.checkOut}`;
    }
    setAppliedSearch(next);
    setSelectedFlightId(null);

    if (!useLive) {
      setLiveFlightHotelCards(null);
      setLiveFlightOffers(null);
      setLiveHubwayzData(null);
      setLiveSearchRequest(null);
      showToast("מצב דמו · fixtures בלבד");
      return;
    }

    const destMeta = UNIFIED_DESTINATIONS.find((d) => d.id === next.destinationId);
    setLiveSearching(true);
    showToast("מחפש מלונות וטיסות");
    try {
      const live = await runUnifiedLiveSearch(next, destMeta);
      setLiveFlightHotelCards(live.flightHotelCards);
      setLiveFlightOffers(live.flightOffers);
      setLiveHubwayzData(live.hubwayzData || live.hotelsRaw || null);
      setLiveSearchRequest(live.searchRequest || null);
      setLiveErrors({ hotels: live.hotelsError, flights: live.flightsError });
      showToast(
        `נמצאו ${live.meta.hotelsCount} מלונות · ${live.meta.flightsCount} טיסות`
      );
    } catch (err) {
      setLiveErrors({ hotels: err?.message || String(err), flights: null });
      showToast("שגיאה בחיפוש חי — בדוק BE / credentials");
    } finally {
      setLiveSearching(false);
    }
  };

  const detailLiveContext = useMemo(() => {
    if (!useLive || !detailPackageId || !liveHubwayzData) return null;
    const resultsCard = (liveFlightHotelCards || []).find(
      (c) => c.id === detailPackageId
    );
    if (!resultsCard) return null;
    return {
      resultsCard,
      flightOffers: liveFlightOffers || [],
      hubwayzData: liveHubwayzData,
      searchRequest: liveSearchRequest,
      appliedSearch,
    };
  }, [
    useLive,
    detailPackageId,
    liveHubwayzData,
    liveFlightHotelCards,
    liveFlightOffers,
    liveSearchRequest,
    appliedSearch,
  ]);

  if (flowStep === "payment" && bookingDraft) {
    return (
      <FlightHotelPaymentPage
        bookingDraft={bookingDraft}
        appliedSearch={appliedSearch}
        hubwayzSearchRequest={liveSearchRequest}
        onBack={() => setFlowStep("passengers")}
        onStage={(id) => {
          if (id === "results") {
            setFlowStep("results");
            setDetailPackageId(null);
            setBookingDraft(null);
          } else if (id === "detail") {
            setFlowStep("detail");
          } else if (id === "passengers") {
            setFlowStep("passengers");
          }
        }}
        onDone={() => {
          showToast("שריון + PROPOSAL נרשמו · ממתין להעברה בנקאית");
          setFlowStep("results");
          setDetailPackageId(null);
          setBookingDraft(null);
        }}
      />
    );
  }

  if (flowStep === "passengers" && bookingDraft) {
    return (
      <FlightHotelPassengersPage
        bookingDraft={bookingDraft}
        appliedSearch={appliedSearch}
        onBack={() => {
          setFlowStep("detail");
        }}
        onResults={() => {
          setBookingDraft(null);
          setDetailPackageId(null);
          setFlowStep("results");
        }}
        onExpiredSearch={() => {
          // Hubwayz a414: offer code died with the search session — full re-search.
          setBookingDraft(null);
          setDetailPackageId(null);
          setFlowStep("results");
          showToast("קוד ההצעה פג · מריצים חיפוש מחדש");
          void runSearch();
        }}
        onContinueToPayment={(payload) => {
          setBookingDraft(payload);
          setFlowStep("payment");
        }}
      />
    );
  }

  if (flowStep === "detail" && detailPackageId) {
    return (
      <FlightHotelRoomSelectionPage
        packageId={detailPackageId}
        liveContext={detailLiveContext}
        onBack={() => {
          setFlowStep("results");
          setDetailPackageId(null);
          setBookingDraft(null);
        }}
        onContinue={(draft) => {
          setBookingDraft(draft);
          setFlowStep("passengers");
        }}
      />
    );
  }

  return (
    <div className="usm-page hrm-mock" dir="rtl">
      <header className="usm-topbar">
        <div className="usm-topbar__brand">
          <span className="usm-topbar__eyebrow">HotelsRiver · Phase 0 Mockup</span>
          <h1 className="usm-topbar__title">חיפוש אחוד</h1>
        </div>
        <p className="usm-topbar__note">
          הזמנת טיסה + מלון לסוכן · עיצוב כמו חבילת נופש · דמו בלבד
        </p>
      </header>

      <form className="usm-search" onSubmit={runSearch} aria-label="סרגל חיפוש אחוד">
        <label className="usm-field">
          <span className="usm-field__label">מוצא</span>
          <input
            className="usm-field__input"
            value={draftSearch.origin}
            onChange={(e) => patchDraft({ origin: e.target.value.toUpperCase() })}
            maxLength={3}
            aria-label="מוצא"
          />
        </label>

        <label className="usm-field usm-field--wide">
          <span className="usm-field__label">יעד</span>
          <select
            className="usm-field__input"
            value={draftSearch.destinationId}
            onChange={(e) => patchDraft({ destinationId: e.target.value })}
            aria-label="יעד"
          >
            {UNIFIED_DESTINATIONS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label className="usm-field">
          <span className="usm-field__label">יציאה</span>
          <input
            type="date"
            className="usm-field__input"
            value={draftSearch.checkIn}
            onChange={(e) => patchDraft({ checkIn: e.target.value })}
          />
        </label>

        <label className="usm-field">
          <span className="usm-field__label">חזרה</span>
          <input
            type="date"
            className="usm-field__input"
            value={draftSearch.checkOut}
            onChange={(e) => patchDraft({ checkOut: e.target.value })}
          />
        </label>

        <div className="usm-field usm-field--occ">
          <span className="usm-field__label">נוסעים וחדרים</span>
          <UnifiedOccupancyField
            rooms={draftSearch.rooms}
            onChange={(rooms) => patchDraft({ rooms })}
          />
        </div>

        <button type="submit" className="usm-search__submit">
          <SearchOutlined aria-hidden />
          חפש
        </button>
      </form>

      <div className="usm-applied" aria-live="polite">
        <span>
          חיפוש פעיל: {appliedSearch.destinationLabel} · {appliedSearch.dateRangeLabel || `${appliedSearch.checkIn} → ${appliedSearch.checkOut}`} ·{" "}
          {occSummary.occupancyLabel}
        </span>
        <label className="usm-applied__live">
          <input
            type="checkbox"
            checked={useLive}
            onChange={(e) => setUseLive(e.target.checked)}
          />
          חיפוש חי
        </label>
        <span className="usm-applied__demo">
          {liveSearching
            ? "טוען…"
            : useLive && liveFlightHotelCards != null
              ? "מקור: מלונות וטיסות"
              : "fixtures דמו"}
        </span>
      </div>

      <div className="usm-tabs" role="tablist" aria-label="סוג תוצאות">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.id;
          const count =
            tab.id === "flightHotel"
              ? flightHotelCardsRaw.length
              : flightOffers.length;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`usm-tab${selected ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon aria-hidden />
              <span>{tab.label}</span>
              <span className="usm-tab__count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="usm-results" role="tabpanel">
        {activeTab === "flightHotel" ? (
          <section className="usm-packages" aria-label="תוצאות טיסה ומלון">
            <div className="usm-fh-filters hrm-sticky usm-fh-filters-sticky">
              <div className="hrm-tools" aria-label="מיון וסינון תוצאות">
                <span className="hrm-tools__count">
                  {liveSearching
                    ? "טוען…"
                    : flightHotelMatched
                      ? `${flightHotelCards.length} תוצאות`
                      : "0 תוצאות"}
                  {appliedSearch.destinationLabel
                    ? ` ב${appliedSearch.destinationLabel}`
                    : ""}
                  {activeFilterCount > 0 && flightHotelMatched
                    ? ` · מתוך ${flightHotelCardsRaw.length}`
                    : ""}
                </span>

                {activeChips.length > 0 ? (
                  <div className="hrm-tools__chips">
                    {activeChips.map((chip) => (
                      <button
                        key={chip.id}
                        type="button"
                        className="hrm-chip"
                        onClick={chip.onRemove}
                      >
                        {chip.label}
                        <ClearOutlined />
                      </button>
                    ))}
                    <button
                      type="button"
                      className="hrm-link"
                      onClick={clearFilters}
                    >
                      ניקוי הכול
                    </button>
                  </div>
                ) : null}

                <div className="hrm-tools__end">
                  <div className="hrm-view-toggle" role="group" aria-label="תצוגה">
                    <button
                      type="button"
                      className={viewMode === "list" ? "is-active" : ""}
                      aria-pressed={viewMode === "list"}
                      onClick={() => setViewMode("list")}
                      title="תצוגת רשימה"
                    >
                      <UnorderedListOutlined />
                    </button>
                    <button
                      type="button"
                      className={viewMode === "grid" ? "is-active" : ""}
                      aria-pressed={viewMode === "grid"}
                      onClick={() => setViewMode("grid")}
                      title="תצוגת גריד"
                    >
                      <AppstoreOutlined />
                    </button>
                  </div>
                  <label className="hrm-sort">
                    <span className="visually-hidden">מיון</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="price-asc">מחיר: מהזול ליקר</option>
                      <option value="price-desc">מחיר: מהיקר לזול</option>
                      <option value="stars">דירוג מלון</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className={`hrm-tools__filter-btn${
                      filtersOpen ? " is-open" : ""
                    }`}
                    aria-expanded={filtersOpen}
                    aria-controls="usm-filters-panel"
                    onClick={() => setFiltersOpen((v) => !v)}
                  >
                    <FilterOutlined />
                    סינון
                    {activeFilterCount > 0 ? (
                      <span className="hrm-tools__filter-count">
                        {activeFilterCount}
                      </span>
                    ) : null}
                  </button>
                </div>
              </div>

              <div
                id="usm-filters-panel"
                className={`hrm-filters-panel${filtersOpen ? " is-open" : ""}`}
                aria-hidden={!filtersOpen}
              >
                <div className="hrm-filters-panel__grid">
                  <label className="hrm-filters-panel__field">
                    <span>חיפוש מלון</span>
                    <input
                      type="search"
                      value={hotelQuery}
                      onChange={(e) => setHotelQuery(e.target.value)}
                      placeholder="שם מלון…"
                    />
                  </label>

                  <fieldset className="hrm-filters-panel__group">
                    <legend>סוג אירוח</legend>
                    {MOCK_BOARD_OPTIONS.map((o) => (
                      <label key={o.id}>
                        <input
                          type="checkbox"
                          checked={selectedBoards.has(o.id)}
                          onChange={() => toggleInSet(setSelectedBoards, o.id)}
                        />
                        {o.shortLabel || o.label}
                      </label>
                    ))}
                  </fieldset>

                  <fieldset className="hrm-filters-panel__group">
                    <legend>לסוכן</legend>
                    {MOCK_AGENT_QUICK_FILTERS.map((f) => (
                      <label key={f.id}>
                        <input
                          type="checkbox"
                          checked={agentQuick.has(f.id)}
                          onChange={() => toggleInSet(setAgentQuick, f.id)}
                        />
                        {f.label}
                      </label>
                    ))}
                  </fieldset>

                  <fieldset className="hrm-filters-panel__group">
                    <legend>כבודה</legend>
                    {MOCK_BAGGAGE_FILTERS.map((o) => (
                      <label key={o.id}>
                        <input
                          type="checkbox"
                          checked={baggageFilter.has(o.id)}
                          onChange={() => toggleInSet(setBaggageFilter, o.id)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </fieldset>

                  <label className="hrm-filters-panel__field">
                    <span>שעת המראה</span>
                    <select
                      value={departWindow}
                      onChange={(e) => setDepartWindow(e.target.value)}
                    >
                      {MOCK_DEPARTURE_WINDOWS.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="hrm-filters-panel__field">
                    <span>מקומות בטיסה</span>
                    <select
                      value={seatsFilter}
                      onChange={(e) => setSeatsFilter(e.target.value)}
                    >
                      {MOCK_SEATS_FILTERS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <fieldset className="hrm-filters-panel__group">
                    <legend>דירוג</legend>
                    {MOCK_STAR_OPTIONS.map((o) => (
                      <label key={o.id}>
                        <input
                          type="checkbox"
                          checked={selectedStars.has(o.id)}
                          onChange={() => toggleInSet(setSelectedStars, o.id)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </fieldset>

                  <fieldset className="hrm-filters-panel__group">
                    <legend>לילות</legend>
                    {MOCK_NIGHTS_OPTIONS.map((o) => (
                      <label key={o.id}>
                        <input
                          type="checkbox"
                          checked={selectedNights.has(o.id)}
                          onChange={() => toggleInSet(setSelectedNights, o.id)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </fieldset>
                </div>
                {activeFilterCount > 0 ? (
                  <div className="hrm-filters-panel__foot">
                    <button
                      type="button"
                      className="hrm-link"
                      onClick={clearFilters}
                    >
                      ניקוי הכול
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {liveSearching ? (
              <DemoEmptyState
                title="מחפש מלונות וטיסות…"
                body="הבקשות רצות במקביל דרך ה־BE. אין המצאת מחירים."
              />
            ) : !flightHotelMatched ? (
              <DemoEmptyState
                title="אין הצעות טיסה + מלון ליעד הזה"
                body={
                  liveErrors.hotels ||
                  "לא התקבלו מלונות ליעד או לתאריכים שנבחרו."
                }
                hint={
                  liveErrors.flights
                    ? `טיסות: ${liveErrors.flights}`
                    : "נסו יעד אחר או בטלו «חי» לצפייה ב־fixtures."
                }
              />
            ) : flightHotelCards.length === 0 ? (
              <div className="hrm-empty">
                <p>אין הצעות שתואמות את הסינון.</p>
                <button type="button" className="hrm-link" onClick={clearFilters}>
                  נקה סינונים
                </button>
              </div>
            ) : (
              <div
                className={
                  viewMode === "grid" ? "hrm-results__grid" : "hrm-results__list"
                }
              >
                {flightHotelCards.map((pkg) => (
                  <DemoPackageCard
                    key={pkg.id}
                    pkg={pkg}
                    viewMode={viewMode}
                    compared={compareIds.has(pkg.id)}
                    favorited={favoriteIds.has(pkg.id)}
                    onToggleCompare={(id) => toggleInSet(setCompareIds, id)}
                    onToggleFavorite={(id) => toggleInSet(setFavoriteIds, id)}
                    onView={(p) => {
                      setDetailPackageId(p.id);
                      setFlowStep("detail");
                      setBookingDraft(null);
                    }}
                    onMoreFlights={(p) =>
                      showToast(
                        `${p.hotelName}: ${p.flightOptionsCount || 0} אפשרויות טיסה`
                      )
                    }
                  />
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className="usm-flights" aria-label="תוצאות טיסות בלבד">
            {liveSearching ? (
              <>
                <h2 className="usm-results__heading">
                  טיסות בלבד · {appliedSearch.destinationLabel}
                </h2>
                <DemoEmptyState title="טוען טיסות LDS…" body="POST /searchFlights" />
              </>
            ) : !flightsMatched ? (
              <>
                <h2 className="usm-results__heading">
                  טיסות בלבד · {appliedSearch.destinationLabel}
                </h2>
                <DemoEmptyState
                  title="אין טיסות ליעד הזה"
                  body={
                    liveErrors.flights ||
                    (useLive
                      ? "LDS לא החזיר עסקאות ליעד/תאריכים."
                      : "במצב דמו יש fixtures רק ל־TLV→LCA.")
                  }
                  hint="לחצו חפש עם יעד IATA מתאים, או עברו לטיסה + מלון."
                />
              </>
            ) : (
              <div className="fe2 usm-flights__fe2">
                <FlightResultsScreen
                  offers={flightOffers}
                  search={fe2Search}
                  filters={
                    useLive && liveFlightOffers != null
                      ? { resultCount: flightOffers.length }
                      : flightMatch.filters
                  }
                  dateStrip={[]}
                  selectedOfferId={selectedFlightId}
                  onSelectOfferId={setSelectedFlightId}
                  onEditSearch={() =>
                    showToast("עריכת חיפוש — השתמשו בסרגל האחוד למעלה")
                  }
                  resultCount={flightOffers.length}
                />
              </div>
            )}
          </section>
        )}
      </div>

      {toast ? (
        <div className="usm-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
